import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

const PLACEHOLDER_ROW_COUNT = 6;

/**
 * Mirrors `PendingInvoicesView`'s row layout so the table doesn't shift
 * vertically when the real data swaps in. Header + N rows of fixed-width
 * skeletons, ending with a small pill on the right that stands in for
 * the pinned Import button column.
 */
export const PendingInvoicesSkeleton: React.FC = () => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('purchases.widgets.pendingInvoices.title'),
        }),
        [t],
    );

    return (
        <section
            aria-busy
            aria-labelledby="pending-invoices-widget-title"
            {...getNodeTestIdProp('pending-invoices-section')}
        >
            <Widget
                className="flex flex-col gap-0 overflow-hidden p-0"
                {...getNodeTestIdProp('pending-invoices-widget')}
            >
                <Widget.Header
                    className="flex items-center justify-between gap-3 border-b border-default-200 px-6 py-5"
                    {...getNodeTestIdProp('pending-invoices-widget-header')}
                >
                    <div
                        className="flex items-center gap-3"
                        {...getNodeTestIdProp('pending-invoices-header-titles')}
                    >
                        <Skeleton
                            className="size-8 shrink-0 rounded-lg"
                            {...getNodeTestIdProp(
                                'pending-invoices-icon-skeleton',
                            )}
                        />
                        <div
                            className="flex flex-col gap-1.5"
                            {...getNodeTestIdProp(
                                'pending-invoices-title-stack',
                            )}
                        >
                            <Widget.Title
                                className="text-base font-semibold leading-none text-foreground"
                                {...getNodeTestIdProp(
                                    'pending-invoices-widget-title',
                                )}
                            >
                                {copy.title}
                            </Widget.Title>
                            <Widget.Description
                                {...getNodeTestIdProp(
                                    'pending-invoices-widget-subtitle',
                                )}
                            >
                                <Skeleton
                                    className="h-3 w-24 rounded-md"
                                    {...getNodeTestIdProp(
                                        'pending-invoices-widget-subtitle-skeleton',
                                    )}
                                />
                            </Widget.Description>
                        </div>
                    </div>
                </Widget.Header>
                <ul
                    className="divide-y divide-default-200"
                    {...getNodeTestIdProp('pending-invoices-grid-skeleton')}
                >
                    {Array.from({ length: PLACEHOLDER_ROW_COUNT }).map(
                        (_, index) => (
                            <li
                                key={index}
                                className="flex items-center gap-4 px-6 py-3.5"
                                {...getNodeTestIdProp(
                                    `pending-invoices-row-placeholder-${index}`,
                                )}
                            >
                                <Skeleton
                                    className="h-3 w-24 rounded-md"
                                    {...getNodeTestIdProp(
                                        `pending-invoices-row-number-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3 flex-1 rounded-md"
                                    {...getNodeTestIdProp(
                                        `pending-invoices-row-supplier-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3 w-28 rounded-md"
                                    {...getNodeTestIdProp(
                                        `pending-invoices-row-date-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3 w-10 rounded-md"
                                    {...getNodeTestIdProp(
                                        `pending-invoices-row-count-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3.5 w-20 rounded-md"
                                    {...getNodeTestIdProp(
                                        `pending-invoices-row-total-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-8 w-24 rounded-lg"
                                    {...getNodeTestIdProp(
                                        `pending-invoices-row-action-placeholder-${index}`,
                                    )}
                                />
                            </li>
                        ),
                    )}
                </ul>
            </Widget>
        </section>
    );
};
